import React, { useState, useEffect } from 'react';
import { Alert, Button, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

/**
 * Component to display usage limits and upgrade prompts
 * This banner shows when users are approaching their usage limits
 */
const UsageLimitBanner = () => {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/usage/suggestions');
        if (response.data.status === 'success') {
          setUsageData(response.data.data);
        } else {
          setError('Failed to load usage data');
        }
      } catch (err) {
        console.error('Error fetching usage data:', err);
        setError('Failed to load usage data');
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      fetchUsage();
    }
  }, [currentUser]);
  
  if (loading) return null; // Don't show anything while loading
  if (error) return null; // Don't show on error
  if (!usageData) return null;
  
  // If user is already on pro plan or usage is low, don't show banner
  if (usageData.stats.planType === 'pro' || !usageData.suggestion) {
    return null;
  }
  
  // Get the most constrained resource
  const clickPercentage = usageData.stats.clicks.limit ? 
    Math.min(100, Math.floor((usageData.stats.clicks.used / usageData.stats.clicks.limit) * 100)) : 0;
    
  const conversionPercentage = usageData.stats.conversions.limit ? 
    Math.min(100, Math.floor((usageData.stats.conversions.used / usageData.stats.conversions.limit) * 100)) : 0;
  
  const highestUsage = Math.max(clickPercentage, conversionPercentage);
  
  // Determine variant based on urgency
  let variant = 'info';
  if (usageData.urgency === 'high') variant = 'warning';
  if (highestUsage >= 95) variant = 'danger';
  
  return (
    <Alert 
      variant={variant} 
      className="mb-4 shadow-sm"
    >
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <Alert.Heading className="h5 mb-1">
            {highestUsage >= 95 ? '⚠️ Usage Limit Almost Reached' : '📊 Usage Update'}
          </Alert.Heading>
          <p className="mb-2">{usageData.suggestion}</p>
          
          <div className="d-flex gap-3 mb-1">
            <div style={{ flex: 1 }}>
              <small className="d-flex justify-content-between text-muted">
                <span>Clicks</span>
                <span>{usageData.stats.clicks.used} / {usageData.stats.clicks.limit}</span>
              </small>
              <ProgressBar 
                now={clickPercentage} 
                variant={clickPercentage > 90 ? 'danger' : clickPercentage > 70 ? 'warning' : 'info'} 
              />
            </div>
            <div style={{ flex: 1 }}>
              <small className="d-flex justify-content-between text-muted">
                <span>Conversions</span>
                <span>{usageData.stats.conversions.used} / {usageData.stats.conversions.limit}</span>
              </small>
              <ProgressBar 
                now={conversionPercentage} 
                variant={conversionPercentage > 90 ? 'danger' : conversionPercentage > 70 ? 'warning' : 'info'} 
              />
            </div>
          </div>
        </div>
        <div className="ms-3">
          <Button 
            as={Link} 
            to="/dashboard/subscription" 
            variant={highestUsage >= 95 ? 'danger' : 'primary'}
            size="sm"
          >
            Upgrade to Pro
          </Button>
        </div>
      </div>
    </Alert>
  );
};

export default UsageLimitBanner;
